export enum LiveUserNotificationPlaces {
  HOME = 'home',
  AI_CONTENT_DETECTOR = 'ai_content_detector',
  AI_TEXT_HUMANIZER = 'ai_text_humanizer',
  CHECK_PLAGIARISM = 'check_plagiarism',
}

export enum LiveUserNotificationVariants {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

type LiveUserNotificationsFeatureConfig = {
  place: LiveUserNotificationPlaces;
  message: string;
  variant: LiveUserNotificationVariants;
};

type SurveyMonkeyFeatureConfig = {
  total_generations: number;
  days_from_registration: number;
  show_page: string;
  survey_id: string;
  profession: string;
};

export enum GB_FEATURE_KEYS {
  POLYLLM_COMPLIANCE_V1 = 'compliance_v1',
  POLYLLM_COMPLIANCE_V2 = 'compliance_v2',
  LIVE_USER_NOTIFICATIONS = 'live_user_notifications',
  EXTENSION_SURVEYMONKEY_INTEGRATION = 'experiment-surveymonkey-integration',
  LATAM_PLAGIARISM_COST_CUTTING = 'experiment-latam-plagiarism-cost-cutting',
  PREMIUM_ANNUAL_MONTHLY_UPGRADE = 'experiment-premium-annual-monthly-upgrade',
  EXTENSION_PROMO_NEW_PLACES = 'experiment-extension-promo-new-places',
  AI_RESEARCHER_VERSION = 'experiment-ai-researcher-version',
  EXPERIMENT_TEST_STICKY_BUCKETING = 'experiment-test-sticky-bucketing',
  EXPERIMENT_TEST_CHAT_POLYLLM = 'experiment-chat-polyllm',
  PRODUCT_FLOWS_FOR_FUNNEL_PAGE = 'experiment-product-funnel-tool-redirect',
  GRAMMAR_CHECKER_IN_TOOLS = 'experiment-grammar-checker-in-tools',
  POLYLLM_USE_AI_PRICING = 'experiment-chatwithai-use-ai-pricing',
  CHATWITHAI_AUTH_FLOW = 'chatwithai-auth-flow',
  OAUTH_PROVIDERS = 'oauth-providers',
  OAUTH_ONE_TAP = 'ab-oauth-one-tap',
  CWA_LOCKED_MODELS_SELECTOR_PAYWALL = 'locked-models-selector-paywall',
}

export const GB_FEATURES = {
  [GB_FEATURE_KEYS.POLYLLM_COMPLIANCE_V1]: {
    key: GB_FEATURE_KEYS.POLYLLM_COMPLIANCE_V1,
    variants: {
      control: false,
      variant_1: true,
    },
    defaultVariant: false,
  },
  [GB_FEATURE_KEYS.POLYLLM_COMPLIANCE_V2]: {
    key: GB_FEATURE_KEYS.POLYLLM_COMPLIANCE_V2,
    variants: {
      control: false,
      variant_1: true,
    },
    defaultVariant: false,
  },
  [GB_FEATURE_KEYS.CHATWITHAI_AUTH_FLOW]: {
    key: GB_FEATURE_KEYS.CHATWITHAI_AUTH_FLOW,
    variants: {
      control: 'passwordless',
      variant_1: 'password',
    },
    defaultVariant: 'passwordless',
  },
  [GB_FEATURE_KEYS.OAUTH_PROVIDERS]: {
    key: GB_FEATURE_KEYS.OAUTH_PROVIDERS,
    variants: {
      control: { google: true, meta: false, microsoft: false, apple: false },
      variant_1: { google: true, meta: true, microsoft: true, apple: true },
    },
    defaultVariant: {
      google: true,
      meta: false,
      microsoft: false,
      apple: false,
    },
  },
  [GB_FEATURE_KEYS.OAUTH_ONE_TAP]: {
    key: GB_FEATURE_KEYS.OAUTH_ONE_TAP,
    variants: {
      control: 'control',
      variant_b: 'variant_b',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.LATAM_PLAGIARISM_COST_CUTTING]: {
    key: GB_FEATURE_KEYS.LATAM_PLAGIARISM_COST_CUTTING,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.PREMIUM_ANNUAL_MONTHLY_UPGRADE]: {
    key: GB_FEATURE_KEYS.PREMIUM_ANNUAL_MONTHLY_UPGRADE,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.EXTENSION_SURVEYMONKEY_INTEGRATION]: {
    key: GB_FEATURE_KEYS.EXTENSION_SURVEYMONKEY_INTEGRATION,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: {
      total_generations: 0,
      days_from_registration: 0,
      profession: 'student',
      show_page: '/',
      survey_id:
        'tRaiETqnLgj758hTBazgd_2FCq31UvmncXYVkjbhPhd5241YDIWzKtfHyoGc_2FOY8MW',
    } as SurveyMonkeyFeatureConfig,
  },
  [GB_FEATURE_KEYS.LIVE_USER_NOTIFICATIONS]: {
    key: GB_FEATURE_KEYS.LIVE_USER_NOTIFICATIONS,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: [
      {
        place: '' as LiveUserNotificationPlaces,
        message: '',
        variant: '' as LiveUserNotificationVariants,
      },
    ] as LiveUserNotificationsFeatureConfig[],
  },
  [GB_FEATURE_KEYS.EXTENSION_PROMO_NEW_PLACES]: {
    key: GB_FEATURE_KEYS.EXTENSION_PROMO_NEW_PLACES,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.AI_RESEARCHER_VERSION]: {
    key: GB_FEATURE_KEYS.AI_RESEARCHER_VERSION,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.EXPERIMENT_TEST_STICKY_BUCKETING]: {
    key: GB_FEATURE_KEYS.EXPERIMENT_TEST_STICKY_BUCKETING,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.EXPERIMENT_TEST_CHAT_POLYLLM]: {
    key: GB_FEATURE_KEYS.EXPERIMENT_TEST_CHAT_POLYLLM,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.PRODUCT_FLOWS_FOR_FUNNEL_PAGE]: {
    key: GB_FEATURE_KEYS.PRODUCT_FLOWS_FOR_FUNNEL_PAGE,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.GRAMMAR_CHECKER_IN_TOOLS]: {
    key: GB_FEATURE_KEYS.GRAMMAR_CHECKER_IN_TOOLS,
    variants: {
      control: 'control',
      variant_1: 'test',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.POLYLLM_USE_AI_PRICING]: {
    key: GB_FEATURE_KEYS.POLYLLM_USE_AI_PRICING,
    variants: {
      control: 'control',
      variant_1: 'variant_b',
    },
    defaultVariant: 'control',
  },
  [GB_FEATURE_KEYS.CWA_LOCKED_MODELS_SELECTOR_PAYWALL]: {
    key: GB_FEATURE_KEYS.CWA_LOCKED_MODELS_SELECTOR_PAYWALL,
    variants: {
      control: 'control',
      variant_b: 'variant_b',
    },
    defaultVariant: 'control',
  },
};

export type GBFeatureKey = keyof typeof GB_FEATURES;

export type FeatureVariant<K extends GBFeatureKey> =
  (typeof GB_FEATURES)[K]['defaultVariant'];
